// The numbers behind the four-tab redesign. Every screen reads from here so
// the three layers can't drift apart between views:
//   Plan awal  – lines as uploaded (in_v1, v1_amount, v1_tanggal; frozen)
//   Disetujui  – approved position: confirmed values, paid amount for done lines
//   Diajukan   – Disetujui plus everything still waiting for the Director
// The server already applies pending amendments to plan_lines live
// ("transitory", see amendments.js) and returns both series in arusKas:
// saldoConfirmed (= Disetujui) and saldo (= Diajukan, minus pending cancels).

// Saldo awal checks ignore gaps smaller than this (Director, 27 Oct: the alert
// showed "+0,00 jt" for a 1-rupiah rounding leftover). Not something to act on.
export const SALDO_TOL = 1000;
export const amtOf = l => Math.max(l.pengeluaran || 0, l.penerimaan || 0);
export const sgnOf = l => (l.pengeluaran > 0 ? -1 : 1);
export const digits = v => parseInt(String(v ?? '').replace(/[^0-9]/g, ''), 10) || 0;
export const dayOf = t => String(t || '').slice(0, 10);

// ── Formatting ──────────────────────────────────────────────
export const rp = n => (n < 0 ? '−' : '') + Math.abs(Math.round(n || 0)).toLocaleString('id-ID');
export const rpS = n => (n > 0 ? '+' : n < 0 ? '−' : '') + Math.abs(Math.round(n || 0)).toLocaleString('id-ID');
export const jt = (n, d = 2) => (n < 0 ? '−' : '') + (Math.abs(n || 0) / 1e6).toLocaleString('id-ID', { minimumFractionDigits: d, maximumFractionDigits: d });
export const jtS = (n, d = 2) => (n > 0 ? '+' : n < 0 ? '−' : '') + (Math.abs(n || 0) / 1e6).toLocaleString('id-ID', { minimumFractionDigits: d, maximumFractionDigits: d });
export const jt1 = n => (n < 0 ? '−' : '') + (Math.abs(n || 0) / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 1 });
const DAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
export const dayName = iso => { const d = new Date(iso + 'T00:00:00'); return `${DAYS[d.getDay()]}, ${d.getDate()} ${d.toLocaleDateString('id-ID', { month: 'short' })}`; };
export const shortDate = iso => { if (!iso) return ''; const d = new Date(String(iso).slice(0, 10) + 'T00:00:00'); return `${d.getDate()} ${d.toLocaleDateString('id-ID', { month: 'short' })}`; };
export const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// ── Categories (Buku Kas › Kategori, Ringkasan) ─────────────
export const CATS = ['Uang Jalan', 'Vendor', 'Operasional', 'Penerimaan', 'Pinjaman Direktur', 'Pindah Rekening'];
export function catOf(l) {
  if (l.tipe === 'Pindah Rekening') return 'Pindah Rekening'; // BCA <-> OCBC, neither income nor cost
  if (l.tipe === 'Pinjaman') return 'Pinjaman Direktur';
  if (!(l.pengeluaran > 0)) return 'Penerimaan';
  if (['UJ Lampung', 'UJ Waste Lampung', 'UJ Balikan', 'Balikan'].includes(l.tipe)) return 'Uang Jalan';
  if (l.tipe === 'Vendor') return 'Vendor';
  return 'Operasional';
}

// ── Build the month model from the dashboard payload ────────
export function buildModel(data, view = 'BCA') {
  if (!data || !data.plan) return null;
  // One business, two wallets (30 Oct). Every line belongs to one bank;
  // `view` picks whose cash this model follows: 'BCA' (default: the account
  // that pays, so saldo warnings, forecast and the approval preview), 'OCBC'
  // (PT AJT) or 'Semua' (both, the total). Business numbers (categories,
  // ritase) always count every line, whichever bank the money used.
  const allLines = data.planLines?.all || [];
  const isO = l => l.rekening === 'OCBC';
  const lines = view === 'Semua' ? allLines : allLines.filter(l => (view === 'OCBC') === isO(l));
  const ocbcLines = allLines.filter(l => isO(l) && l.status !== 'cancelled');
  const ocbcAmt = l => (l.pengeluaran > 0 ? -1 : 1) * (l.status === 'disbursed' && l.paid_amount != null ? l.paid_amount : Math.max(l.pengeluaran || 0, l.penerimaan || 0));
  const ocbcStart = data.plan.saldoAwalOcbc || 0;
  const ocbc = { start: ocbcStart, lines: ocbcLines, amt: ocbcAmt, any: ocbcStart !== 0 || ocbcLines.length > 0,
    planEnd: ocbcStart + ocbcLines.reduce((t, l) => t + ocbcAmt(l), 0),
    actual: ocbcStart + ocbcLines.filter(l => l.status === 'disbursed').reduce((t, l) => t + ocbcAmt(l), 0) };
  const amendments = data.amendments?.items || [];
  const requests = data.requests?.items || [];
  const pending = amendments.filter(a => a.status === 'pending');
  const pendingById = Object.fromEntries(pending.map(a => [a.id, a]));
  const daily = data.arusKas?.daily || [];
  const monthKey = data.plan.month_key || (daily[0]?.date || '').slice(0, 7);
  const nDays = daily.length || 30;
  const bcaStart = data.plan.saldoAwal ?? data.arusKas?.saldoAwal ?? 0;
  const saldoAwal = view === 'BCA' ? bcaStart : view === 'OCBC' ? ocbcStart : bcaStart + ocbcStart;
  // the server's daily paths: saldo/saldoConfirmed are BCA, saldoOcbc* OCBC
  const pick = (d, b, o) => (view === 'BCA' ? d[b] : view === 'OCBC' ? (d[o] ?? ocbcStart) : d[b] + (d[o] ?? ocbcStart));
  const dates = daily.map(d => d.date);
  const idxOf = iso => dates.indexOf(dayOf(iso));

  const pendFor = l => (l.pending_amendment_ids || []).map(id => pendingById[id]).filter(Boolean);
  const isPendCancel = l => pendFor(l).some(a => a.amendment_type === 'cancel_line');
  const isPendAdd = l => pendFor(l).some(a => ['add_line', 'add_disbursement', 'add_inflow'].includes(a.amendment_type));
  const oldestOf = (l, type) => pendFor(l).filter(a => a.amendment_type === type).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];

  // Amounts in each layer for one line (signed, cash direction).
  const paidOr = (l, v) => (l.status === 'disbursed' && l.paid_amount != null ? l.paid_amount : v);
  const confirmedAmt = l => {
    const ac = oldestOf(l, 'amount_change');
    return paidOr(l, ac ? digits(ac.original_value) : amtOf(l));
  };
  const confirmedDate = l => {
    const dc = oldestOf(l, 'date_change');
    return dc && /^\d{4}-\d{2}-\d{2}/.test(dc.original_value || '') ? dayOf(dc.original_value) : dayOf(l.tanggal);
  };
  const inV1 = l => !!l.in_v1;
  const inConfirmed = l => l.status !== 'cancelled' && !isPendAdd(l);
  const inTentative = l => l.status !== 'cancelled' && !isPendCancel(l);

  // Three daily saldo paths (cumulative, index 0 = day 1).
  const v1Net = Array(nDays).fill(0);
  lines.filter(inV1).forEach(l => { const i = idxOf(l.v1_tanggal || l.tanggal); if (i >= 0) v1Net[i] += sgnOf(l) * (l.v1_amount ?? amtOf(l)); });
  const cum = arr => { let s = saldoAwal; return arr.map(v => (s += v)); };
  const v1 = cum(v1Net);
  const settled = daily.map(d => pick(d, 'saldoConfirmed', 'saldoOcbcConfirmed'));
  const tentative = daily.map(d => pick(d, 'saldo', 'saldoOcbc'));
  const settledNet = settled.map((v, i) => v - (i ? settled[i - 1] : saldoAwal));
  // Only what actually happened (paid/received lines, at the amount paid):
  // the series to hold against the bank. settled also carries lines that
  // were planned for a past day but never paid.
  const actualNet = Array(nDays).fill(0);
  lines.filter(l => l.status === 'disbursed').forEach(l => { const i = idxOf(l.tanggal); if (i >= 0) actualNet[i] += sgnOf(l) * paidOr(l, amtOf(l)); });
  const actual = cum(actualNet);

  // Lines flagged as having changed since plan awal, per day (calendar dots).
  const changedPerDay = Array(nDays).fill(0);
  lines.forEach(l => {
    const changed = !inV1(l) || l.status === 'cancelled' || (l.v1_amount != null && l.v1_amount !== amtOf(l)) || (l.v1_tanggal && dayOf(l.v1_tanggal) !== dayOf(l.tanggal)) || pendFor(l).length;
    const i = idxOf(l.tanggal); if (changed && i >= 0) changedPerDay[i] += 1;
  });

  // ── Pending items (everything waiting for the Director) ──
  // Each gets a per-day effect so the approval panel can recompute the
  // month for any subset the Director ticks.
  const lineById = Object.fromEntries(lines.map(l => [l.id, l]));
  // an OCBC line's change moves nothing in BCA (no lineById entry, no effect), but keeps its name
  const anyById = Object.fromEntries(allLines.map(l => [l.id, l]));
  const items = [];
  pending.forEach(a => {
    const l = lineById[a.plan_line_id] || null;
    const eff = Array(nDays).fill(0);
    let kind = a.amendment_type, before = '', after = '', v1Val = '—';
    if (a.amendment_type === 'amount_change' && l) {
      const o = digits(a.original_value), n = digits(a.requested_value);
      const i = idxOf(l.tanggal); if (i >= 0) eff[i] += sgnOf(l) * (n - o);
      before = rp(o); after = rp(n); v1Val = l.in_v1 ? rp(l.v1_amount) : '—';
    } else if (a.amendment_type === 'date_change' && l) {
      const from = idxOf(a.original_value), to = idxOf(a.requested_value), v = sgnOf(l) * paidOr(l, amtOf(l));
      if (from >= 0) eff[from] -= v; if (to >= 0) eff[to] += v;
      before = shortDate(a.original_value); after = shortDate(a.requested_value); v1Val = l.in_v1 ? shortDate(l.v1_tanggal) : '—';
    } else if (a.amendment_type === 'cancel_line' && l) {
      const i = idxOf(l.tanggal); if (i >= 0) eff[i] -= sgnOf(l) * confirmedAmt(l);
      before = rpS(sgnOf(l) * confirmedAmt(l)); after = 'batal'; v1Val = l.in_v1 ? rpS(sgnOf(l) * l.v1_amount) : '—';
    } else if (['add_disbursement', 'add_inflow', 'add_line'].includes(a.amendment_type)) {
      let v = {}; try { v = JSON.parse(a.requested_value); } catch {}
      const i = idxOf(v.tanggal); const val = (v.penerimaan || 0) - (v.pengeluaran || 0);
      // a new line moves only its own bank's saldo; the Director still sees which bank
      if (i >= 0 && (view === 'Semua' || (view === 'OCBC') === (v.rekening === 'OCBC'))) eff[i] += val;
      before = '—'; after = rpS(val) + (v.rekening === 'OCBC' ? ' · OCBC' : '');
    } else {
      before = a.original_value || ''; after = a.requested_value || '';
    }
    const title = l?.keterangan || anyById[a.plan_line_id]?.keterangan || (() => { try { return JSON.parse(a.requested_value).keterangan; } catch { return 'Baris baru'; } })();
    items.push({ key: 'a:' + a.id, src: 'amendment', id: a.id, kind, title, date: l?.tanggal || null, reason: a.reason, by: a.requested_by, at: a.created_at, v1Val, before, after, eff, delta: eff.reduce((s, x) => s + x, 0), lineId: a.plan_line_id });
  });
  // Payments sent at a different amount than their line (Exception tier).
  requests.filter(r => r.status === 'pending').forEach(r => {
    const l = lineById[r.plan_line_id] || null;
    const eff = Array(nDays).fill(0);
    if (l) { const i = idxOf(l.tanggal); if (i >= 0) eff[i] += sgnOf(l) * (r.amount - amtOf(l)); }
    items.push({ key: 'r:' + r.id, src: 'request', id: r.id, kind: 'catat_bayar', title: r.keterangan, date: l?.tanggal || r.request_date, reason: r.ops_note || '', by: r.requested_by, at: r.created_at, v1Val: l?.in_v1 ? rp(l.v1_amount) : '—', before: l ? rp(amtOf(l)) : '—', after: rp(r.amount), eff, delta: eff.reduce((s, x) => s + x, 0), lineId: r.plan_line_id });
  });

  // ── GPS check per paid trip line ──
  // The nightly match in skl-route-agent pairs each ritase row with the GPS
  // trip it paid for (dashboard.js attaches it as request.gps once checked);
  // ops_note marks a payment approved without a truck, so its ritase row is
  // missing. Same signals the old Requests tab showed as tags.
  const RIT = ['UJ Lampung', 'UJ Waste Lampung', 'UJ Balikan', 'Balikan'];
  const paidReq = {};
  requests.filter(r => r.status === 'approved' && r.plan_line_id).forEach(r => { paidReq[r.plan_line_id] = r; });
  // No GPS trip, and the trip this payment would fit was already taken by
  // another payment of the same truck and kind: the GPS saw one trip for two
  // payments. Say that, it's the question to answer (Director, 27 Oct: "why
  // are the things that you need to ask aurel not in the notifications?").
  // Same window as ritase_match.py: trip left 8 days before .. 1 day after.
  const fam = t => (t === 'UJ Balikan' || t === 'Balikan' ? 'balik' : t);
  const takenBy = r => {
    const paid = Date.parse(String(r.request_date).slice(0, 10) + 'T00:00:00+07:00');
    return requests.filter(o => o.id !== r.id && o.status === 'approved' && o.no_truk === r.no_truk && fam(o.tipe) === fam(r.tipe) && o.gps?.at
      && Date.parse(o.gps.at) >= paid - 8 * 864e5 && Date.parse(o.gps.at) < paid + 2 * 864e5)
      .sort((a, b) => Math.abs(Date.parse(a.gps.at) - paid) - Math.abs(Date.parse(b.gps.at) - paid))[0] || null;
  };
  const gpsOf = l => {
    const r = paidReq[l.id];
    if (!r || l.status !== 'disbursed') return null;
    if (r.ops_note) return { state: 'flag', note: r.ops_note, req: r };
    if (!RIT.includes(l.tipe)) return null;
    if (r.gps?.label) return { state: 'ok', label: r.gps.label, req: r };
    // A recent payment whose trip is still on the road has nothing to match
    // yet: the match pairs a trip only once it has arrived (30 Oct: UJ CSK
    // Bogor 9507, paid 28 Okt, left 27 Okt, reached Bogor 30 Okt 02:44, 14
    // minutes after the 02:30 match, and was called "1 trip GPS untuk 2 bayar?").
    // Lampung → Bogor/Karawang takes up to ~3 days, so give it 4 before asking.
    if (r.gps && Date.now() - Date.parse(String(r.request_date).slice(0, 10) + 'T00:00:00+07:00') < 4 * 864e5) return { state: 'wait', onRoad: true, picked: r.trip_label || null, req: r };
    if (r.gps) return { state: 'none', req: r, taken: takenBy(r) };
    return { state: 'wait', picked: r.trip_label || null, req: r };
  };
  const gpsIssues = lines.filter(l => ['flag', 'none'].includes(gpsOf(l)?.state));
  // Recorded automatically from the bank (services/autoRecord.js); undoable for 7 days.
  const autoOf = l => { const r = paidReq[l.id]; return l.status === 'disbursed' && r && r.source === 'accurate_auto' ? { req: r, undoable: Date.now() - Date.parse(r.created_at) < 7 * 864e5 } : null; };
  const autoLines = lines.filter(l => autoOf(l));

  // ── Overdue ("terlambat"): planned date passed, unpaid in the app and
  // not seen in the bank (match: /api/v2/bankmatch). Director, 2 Oct.
  const lateDays = (l, today) => Math.round((Date.parse(today) - Date.parse(confirmedDate(l))) / 864e5);
  // A line already moved to today or later (pending date change) is not overdue:
  // she acted on it, it's waiting for the Director (15 Oct: otherwise it showed
  // on its old day and again under today).
  const movedOn = (l, today) => pendFor(l).some(a => a.amendment_type === 'date_change') && dayOf(l.tanggal) >= today;
  const overdueLines = (match, today) => lines.filter(l => l.status === 'upcoming' && !isPendAdd(l) && !isPendCancel(l) && !movedOn(l, today)
    && confirmedDate(l) < today && !['bank_paid', 'bank_paid_diff'].includes(match?.lines?.[l.id]?.state))
    .map(l => ({ line: l, late: lateDays(l, today) })).sort((a, b) => b.late - a.late || amtOf(b.line) - amtOf(a.line));
  // "Perkiraan" daily path (option b): the approved path, but overdue lines
  // leave on today instead of their planned day. Only for the month that
  // contains today; nothing approved changes, month-end is the same.
  const forecastPath = (overdue, today) => {
    const ti = idxOf(today);
    if (ti < 0 || !overdue.length) return null;
    const delta = Array(nDays).fill(0);
    overdue.forEach(({ line: l }) => {
      const pi = idxOf(confirmedDate(l)); if (pi < 0 || pi >= ti) return;
      const signed = sgnOf(l) * confirmedAmt(l);
      for (let k = pi; k < ti; k++) delta[k] -= signed; // not gone yet on these days
    });
    return settled.map((v, k) => v + delta[k]);
  };

  // Month-end in each layer.
  const v1End = v1[nDays - 1] ?? saldoAwal;
  const settledEnd = settled[nDays - 1] ?? saldoAwal;
  const tentativeEnd = settledEnd + items.reduce((s, it) => s + it.delta, 0);

  // ── Category totals (4 columns): the whole business, every bank ──
  const cats = Object.fromEntries(CATS.map(c => [c, { v1: 0, settled: 0, tentative: 0, paid: 0, n: 0, paidN: 0 }]));
  allLines.forEach(l => {
    const c = cats[catOf(l)];
    if (inV1(l)) c.v1 += sgnOf(l) * (l.v1_amount ?? amtOf(l));
    if (inConfirmed(l)) c.settled += sgnOf(l) * confirmedAmt(l);
    if (inTentative(l)) { c.tentative += sgnOf(l) * paidOr(l, amtOf(l)); c.n += 1; }
    if (l.status === 'disbursed') { c.paid += sgnOf(l) * paidOr(l, amtOf(l)); c.paidN += 1; }
  });

  // ── Ritase: done / planned now / planned at plan awal ──
  const ritKind = l => (l.tipe === 'UJ Lampung' ? 'pergi' : l.tipe === 'UJ Waste Lampung' ? 'waste'
    : ((l.tipe === 'UJ Balikan' || l.tipe === 'Balikan') && l.pengeluaran > 0) ? 'balikan' : null); // one per balikan UJ (dashboard.js isPlannedBalikan)
  const ritV1 = { pergi: 0, balikan: 0, waste: 0 };
  allLines.filter(inV1).forEach(l => { const k = ritKind(l); if (k) ritV1[k] += 1; });
  const rit = { done: { pergi: data.ritase?.pergi || 0, balikan: data.ritase?.balikan || 0, waste: data.ritase?.waste || 0 }, planned: data.ritase?.planned || { pergi: 0, balikan: 0, waste: 0 }, v1: ritV1 };

  return { view, allLines, anyById, isOcbc: isO, monthKey, saldoAwal, ocbc, dates, nDays, lines, lineById, pendFor, isPendCancel, isPendAdd, confirmedAmt, confirmedDate, paidOr,
    v1, settled, tentative, actual, settledNet, changedPerDay, items, v1End, settledEnd, tentativeEnd, cats, rit, idxOf, pending, gpsOf, gpsIssues, paidReq, autoOf, autoLines, lateDays, overdueLines, forecastPath };
}

// Daily path if only the selected pending items were approved.
export function pathWith(model, keys, extra = []) {
  const eff = Array(model.nDays).fill(0);
  [...model.items, ...extra].filter(it => keys.has(it.key)).forEach(it => it.eff.forEach((v, i) => { eff[i] += v; }));
  let acc = 0;
  return model.settled.map((v, i) => v + (acc += eff[i]));
}
